/*
Copyright (C) 2020 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React, { useEffect, useState } from 'react';
import { includes } from 'lodash';

import useEventCallback from '@broadleaf/admin-components/dist/common/hooks/useEventCallback';
import { getEndpointByType } from '@broadleaf/admin-components/dist/metadata/utils/MetadataUtils';
import { request } from '@broadleaf/admin-components/dist/metadata/utils/request';
import FulfillmentAction from '@broadleaf/admin-components/dist/oms/components/FulfillmentAction';
import { queueFetch } from '@broadleaf/admin-components/dist/oms/hooks/useFufillmentState/state/fulfillmentActions';
import {
  IMetadata,
  IMetadataAction
} from '@broadleaf/admin-components/dist/types/metadata';
import { IFulfillmentViewLocalState } from '@broadleaf/admin-components/dist/oms/components/FulfillmentView/fulfillment';
import UpdateFulfillmentSettingsModal from '../../UpdateFulfillmentSettingsModal';
import log from '@broadleaf/admin-components/dist/common/utils/log';
import { IOrderFulfillmentSettings } from '../../../utils/RmFulfillmentUtils';
import { IOrderViewLocalState } from '@broadleaf/admin-components/dist/oms/components';
import { IOrderFulfillment } from '@broadleaf/admin-components/dist/types/oms';
import useContextParams from '../../hooks/useContextParams';

const logger = log.getLogger(
  'oms.components.FulfillmentView.action-components.UpdateFulfillmentSettings'
);

export interface StatusChangeProps {
  actionDefinition: IMetadataAction;
  // the order* props are used in the RmItemsOrderViewSection
  orderDispatch?: Function;
  orderMetadata?: IMetadata;
  orderState?: IOrderViewLocalState;
  // the standard props need to fallback to the order* props
  dispatch?: Function;
  metadata?: IMetadata;
  state?: IFulfillmentViewLocalState | IOrderViewLocalState;
  fulfillment?: IOrderFulfillment;
}

const UpdateFulfillmentSettings: React.FC<StatusChangeProps> = ({
  actionDefinition,
  orderDispatch,
  orderMetadata,
  orderState,
  dispatch = orderDispatch,
  // the standard props need to fallback to the order* props
  metadata = orderMetadata,
  state = orderState ||
    ({} as IFulfillmentViewLocalState | IOrderViewLocalState),
  fulfillment = state?.data as IOrderFulfillment
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const contextParams = useContextParams(metadata, fulfillment);
  const { attributes = {}, label } = actionDefinition;
  const {
    allowedStatuses = [],
    fulfillmentTypeAllowedStatuses = {},
    fulfillmentType = ''
  } = attributes as Record<string, any>;
  const handleClose = () => setIsOpen(false);
  const [settingsLoaded, setSettingsLoaded] = useState<boolean>(false);
  const handleSubmit = useHandleSubmit({
    actionDefinition,
    dispatch,
    metadata,
    fulfillment,
    setIsSubmitting,
    setSettingsLoaded
  });
  const readEndpoint = getEndpointByType(
    actionDefinition,
    'READ_FULFILLMENT_SETTINGS'
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [settings, setSettings] = useState<IOrderFulfillmentSettings>(null);
  const allAllowedStatuses = allowedStatuses.concat(
    fulfillmentTypeAllowedStatuses[fulfillment.type]
  );

  if (
    !includes(allAllowedStatuses, fulfillment.status) ||
    (fulfillmentType !== '' && fulfillmentType !== fulfillment.type)
  ) {
    return null;
  }

  return (
    <>
      <FulfillmentAction
        color="black"
        disabled={state.isFetching || loading}
        onClick={async () => {
          await refreshSettings(
            setLoading,
            setSettings,
            setSettingsLoaded,
            readEndpoint,
            fulfillment,
            contextParams
          )();
          setIsOpen(true);
        }}
      >
        {label}
      </FulfillmentAction>

      {isOpen && settingsLoaded && settings && (
        <UpdateFulfillmentSettingsModal
          metadata={actionDefinition}
          fulfillment={fulfillment}
          fulfillmentSettings={settings}
          onClose={handleClose}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          title={label}
        />
      )}
    </>
  );
};

function refreshSettings(
  setLoading,
  setSettings,
  setSettingsLoaded,
  readEndpoint,
  fulfillment,
  contextParams
) {
  return async () => {
    try {
      setLoading(true);
      const { data } = await request(
        { method: 'get', ...readEndpoint, data: fulfillment },
        contextParams
      );
      setSettings(data);
    } catch (err) {
      if (err.cancelled) {
        return;
      }
      logger.error(
        `An error occurred trying to read the print label status for fulfillment ${contextParams.id}`
      );
    } finally {
      setLoading(false);
      setSettingsLoaded(true);
    }
  };
}

function useHandleSubmit({
  actionDefinition,
  dispatch,
  metadata,
  fulfillment,
  setIsSubmitting,
  setSettingsLoaded
}) {
  const contextParams = useContextParams(metadata, fulfillment);
  const submitEndpoint = getEndpointByType(
    actionDefinition,
    'UPDATE_FULFILLMENT_SETTINGS'
  );
  return useEventCallback(
    async values => {
      setIsSubmitting(true);
      const data = {
        ...values,
        reason: values.reason?.value
      };
      try {
        await request(
          {
            method: 'post',
            ...submitEndpoint,
            data
          },
          contextParams
        );
      } finally {
        setIsSubmitting(false);
        // We need to refetch the settings after an update
        setSettingsLoaded(false);
        dispatch(queueFetch());
      }
    },
    [submitEndpoint, contextParams, setIsSubmitting]
  );
}

export default UpdateFulfillmentSettings;
